import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Building2,
  Search,
  Plus,
  Grid3X3,
  Table as TableIcon,
  Columns3,
  Layers,
  UserCheck,
  Compass,
  Maximize2,
  Check,
  Calculator,
} from "lucide-react";
import { useDataProvider, useGetIdentity, useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CostSheetCalculatorModal } from "./CostSheetCalculatorModal";
import { useRealEstateContext } from "../providers/RealEstateContext";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Property, PropertyType, UnitStatus } from "@/types/realEstate";
import { formatPriceInINR } from "@/lib/formatters";
import { getStatusBadge } from "./propertyUtils";
import {
  canModifyUnitHold,
  canTransitionUnitStatus,
  canUnlinkLeadFromUnit,
  defaultHoldUntilIso,
  HOLD_RESTRICTED_ERROR,
  requiresDiscountApproval,
} from "../bookings/bookingUtils";

import { ActionPopupModal } from "../misc/ActionPopupModal";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileHeader from "../layout/MobileHeader";
import { cn } from "@/lib/utils";

const BOOKING_ELIGIBLE_STATUSES: UnitStatus[] = [
  "Available",
  "On_Hold",
  "Token_Received",
];

const toDatetimeLocalValue = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const UNIT_STATUSES: UnitStatus[] = [
  "Available",
  "On_Hold",
  "Token_Received",
  "Booked",
  "Sold",
];

/** Status cell colors for the stacking matrix (aligned with getStatusBadge). */
export const UNIT_STATUS_STACK_COLORS: Record<UnitStatus, string> = {
  Available: "bg-emerald-600 hover:bg-emerald-700 text-white",
  On_Hold: "bg-purple-600 hover:bg-purple-700 text-white",
  Token_Received: "bg-amber-500 hover:bg-amber-600 text-white",
  Booked: "bg-blue-600 hover:bg-blue-700 text-white",
  Sold: "bg-slate-700 hover:bg-slate-800 text-white",
};

const TOWER_FROM_UNIT_RE = /^([A-Za-z][A-Za-z0-9]*)\s*[-/]\s*.+$/;
const FLOOR_FROM_UNIT_RE = /^[A-Za-z][A-Za-z0-9]*\s*[-/]\s*(\d{3,4})$/;

/** Tower from unit_number prefix (e.g. A-1402 → "A"). No tower/wing DB column. */
export function parseUnitTower(unitNumber: string): string | null {
  const m = unitNumber.trim().match(TOWER_FROM_UNIT_RE);
  return m ? m[1].toUpperCase() : null;
}

/**
 * Floor from floor_number, else reliable unit_number heuristic
 * (A-1402 → 14, A-101 → 1). Returns null when unknown.
 */
export function resolveUnitFloor(property: Property): number | null {
  if (
    property.floor_number != null &&
    Number.isFinite(Number(property.floor_number))
  ) {
    return Number(property.floor_number);
  }
  const m = property.unit_number.trim().match(FLOOR_FROM_UNIT_RE);
  if (!m) return null;
  const digits = m[1];
  return digits.length === 4
    ? parseInt(digits.slice(0, 2), 10)
    : parseInt(digits.slice(0, 1), 10);
}

export type UnitStackFloorRow = {
  floor: number | null;
  floorLabel: string;
  units: Property[];
};

export type UnitStackTower = {
  tower: string;
  floors: UnitStackFloorRow[];
};

export type UnitStackProject = {
  projectId: string;
  projectName: string;
  towers: UnitStackTower[];
};

/** Build Tower → Floor → Unit matrix from a filtered property list. */
export function buildUnitStackMatrix(
  propertiesList: Property[],
): UnitStackProject[] {
  type FloorBucket = Map<string, Property[]>; // floorKey → units
  type TowerBucket = Map<string, FloorBucket>;
  const byProject = new Map<string, { name: string; towers: TowerBucket }>();

  for (const prop of propertiesList) {
    const projectId = prop.project_id || "unknown";
    const projectName = prop.project?.name || "Independent Project";
    let project = byProject.get(projectId);
    if (!project) {
      project = { name: projectName, towers: new Map() };
      byProject.set(projectId, project);
    }

    const tower = parseUnitTower(prop.unit_number) ?? "—";
    let floors = project.towers.get(tower);
    if (!floors) {
      floors = new Map();
      project.towers.set(tower, floors);
    }

    const floor = resolveUnitFloor(prop);
    const floorKey = floor == null ? "null" : String(floor);
    let units = floors.get(floorKey);
    if (!units) {
      units = [];
      floors.set(floorKey, units);
    }
    units.push(prop);
  }

  const projects: UnitStackProject[] = [];
  for (const [projectId, project] of byProject) {
    const towers: UnitStackTower[] = [...project.towers.entries()]
      .sort(([a], [b]) => {
        if (a === "—") return 1;
        if (b === "—") return -1;
        return a.localeCompare(b, undefined, { numeric: true });
      })
      .map(([tower, floorMap]) => {
        const floors: UnitStackFloorRow[] = [...floorMap.entries()]
          .map(([floorKey, units]) => {
            const floor = floorKey === "null" ? null : Number(floorKey);
            return {
              floor,
              floorLabel: floor == null ? "—" : String(floor),
              units: [...units].sort((a, b) =>
                a.unit_number.localeCompare(b.unit_number, undefined, {
                  numeric: true,
                }),
              ),
            };
          })
          .sort((a, b) => {
            // Top floor first (stacking); unknown floors last
            if (a.floor == null && b.floor == null) return 0;
            if (a.floor == null) return 1;
            if (b.floor == null) return -1;
            return b.floor - a.floor;
          });
        return { tower, floors };
      });

    projects.push({
      projectId,
      projectName: project.name,
      towers,
    });
  }

  return projects.sort((a, b) =>
    a.projectName.localeCompare(b.projectName, undefined, { numeric: true }),
  );
}

export const BUDGET_PRESETS = [
  { value: "all", label: "Any budget", min: 0, max: Infinity },
  { value: "u1cr", label: "Under ₹1 Cr", min: 0, max: 10000000 },
  { value: "1-2cr", label: "₹1–2 Cr", min: 10000000, max: 20000000 },
  { value: "2-4cr", label: "₹2–4 Cr", min: 20000000, max: 40000000 },
  { value: "4cr+", label: "₹4 Cr+", min: 40000000, max: Infinity },
] as const;

/** Pure filter used by PropertyCatalog — unit-tested. */
export function filterPropertiesList(
  propertiesList: Property[],
  opts: {
    searchQuery: string;
    selectedProject: string;
    selectedType: string;
    selectedStatus: string;
    selectedBudget: string;
  },
): Property[] {
  const {
    searchQuery,
    selectedProject,
    selectedType,
    selectedStatus,
    selectedBudget,
  } = opts;
  const budget = BUDGET_PRESETS.find((b) => b.value === selectedBudget);
  const q = searchQuery.toLowerCase();

  return propertiesList.filter((prop) => {
    const matchesSearch =
      prop.unit_number.toLowerCase().includes(q) ||
      prop.configuration.toLowerCase().includes(q) ||
      (prop.facing && prop.facing.toLowerCase().includes(q)) ||
      (prop.project?.name && prop.project.name.toLowerCase().includes(q));

    const matchesProject =
      selectedProject === "all" || prop.project_id === selectedProject;
    const matchesType =
      selectedType === "all" || prop.property_type === selectedType;
    const matchesStatus =
      selectedStatus === "all" || prop.status === selectedStatus;
    const price = Number(prop.base_price) || 0;
    const matchesBudget =
      !budget ||
      budget.value === "all" ||
      (price >= budget.min && price < budget.max);

    return (
      matchesSearch &&
      matchesProject &&
      matchesType &&
      matchesStatus &&
      matchesBudget
    );
  });
}

export const PropertyCatalog = () => {
  const {
    properties,
    projects,
    addProperty,
    updateProperty,
    deleteProperty,
    upsertPropertyBrochure,
    propertyMedia,
    campaignAudience,
    refreshCampaignAudience,
    transitionUnitStatus,
    holdUnitForLead,
    createBooking,
    releaseUnitHold,
    unlinkLeadFromProperty,
    bookedContactByPropertyId,
    refreshBookedContacts,
  } = useRealEstateContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const { identity } = useGetIdentity();
  const { restrictHoldChangesToHolder } = useConfigurationContext();
  const isManager = Boolean(
    (identity as { administrator?: boolean } | undefined)?.administrator,
  );

  const canChangeHold = (prop: Property) =>
    prop.status !== "On_Hold" ||
    canModifyUnitHold({
      restrictHoldChangesToHolder,
      heldBy: prop.held_by,
      // identity.id is sales.id (see supabase authProvider getIdentity)
      currentSalesId: identity?.id != null ? String(identity.id) : null,
      isManager,
    });

  useEffect(() => {
    void refreshCampaignAudience();
  }, [refreshCampaignAudience]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<
    "grid" | "table" | "kanban" | "stack"
  >("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState<string>("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedBudget, setSelectedBudget] = useState<string>("all");
  const [matchingProperty, setMatchingProperty] = useState<Property | null>(
    null,
  );
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [matchNote, setMatchNote] = useState("");
  const [matchingBusy, setMatchingBusy] = useState(false);
  const [holdUnitChecked, setHoldUnitChecked] = useState(true);
  const [holdUntilLocal, setHoldUntilLocal] = useState(() =>
    toDatetimeLocalValue(defaultHoldUntilIso(48)),
  );
  const [lastMatchedDealId, setLastMatchedDealId] = useState<
    string | number | null
  >(null);
  /** True after Match Lead succeeds, or when opening Book on an already-linked unit. */
  const [matchConfirmed, setMatchConfirmed] = useState(false);
  const [negotiatedPrice, setNegotiatedPrice] = useState("");
  const [bookingBusy, setBookingBusy] = useState(false);
  const [releaseBusyId, setReleaseBusyId] = useState<string | null>(null);
  const [unlinkBusyId, setUnlinkBusyId] = useState<string | null>(null);
  const [viewingProperty, setViewingProperty] = useState<Property | null>(null);
  const [costSheetProperty, setCostSheetProperty] = useState<Property | null>(
    null,
  );
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(
    null,
  );
  const [brochureUrl, setBrochureUrl] = useState("");
  const [savingUnit, setSavingUnit] = useState(false);
  const [popupInfo, setPopupInfo] = useState<{
    title: string;
    desc: string;
  } | null>(null);

  // New / edit property form state
  const [newProp, setNewProp] = useState({
    unit_number: "",
    configuration: "3BHK",
    property_type: "Flat" as PropertyType,
    carpet_area_sqft: "1500",
    base_price: "15000000",
    facing: "East",
    project_id: "",
  });

  const propertiesList = properties;
  const defaultProjects = projects;

  const brochureFor = (propertyId: string) =>
    propertyMedia.find(
      (m) =>
        String(m.property_id) === String(propertyId) &&
        m.media_type === "Brochure_PDF",
    );

  useEffect(() => {
    if (!newProp.project_id && projects[0]?.id) {
      setNewProp((prev) => ({ ...prev, project_id: String(projects[0].id) }));
    }
  }, [projects, newProp.project_id]);

  useEffect(() => {
    if (searchParams.get("create") === "1") {
      openCreateUnit();
      const next = new URLSearchParams(searchParams);
      next.delete("create");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per create=1
  }, [searchParams, setSearchParams]);

  // Filtered properties
  const filteredProperties = useMemo(
    () =>
      filterPropertiesList(propertiesList, {
        searchQuery,
        selectedProject,
        selectedType,
        selectedStatus,
        selectedBudget,
      }),
    [
      propertiesList,
      searchQuery,
      selectedProject,
      selectedType,
      selectedStatus,
      selectedBudget,
    ],
  );

  const stackMatrix = useMemo(
    () => buildUnitStackMatrix(filteredProperties),
    [filteredProperties],
  );

  const resetMatchDialog = () => {
    setMatchingProperty(null);
    setSelectedLeadId("");
    setMatchNote("");
    setHoldUnitChecked(true);
    setHoldUntilLocal(toDatetimeLocalValue(defaultHoldUntilIso(48)));
    setLastMatchedDealId(null);
    setMatchConfirmed(false);
    setNegotiatedPrice("");
  };

  const linkedContactFor = (property: Property) =>
    bookedContactByPropertyId.get(String(property.id));

  const matchLeadButtonLabel = (property: Property) =>
    linkedContactFor(property) ? "Change Lead" : "Match Lead";

  const canUnlinkLead = (property: Property) => {
    const linked = linkedContactFor(property);
    if (!linked) return false;
    return canUnlinkLeadFromUnit(
      property.status,
      Boolean(linked.has_active_booking),
    );
  };

  /** Open match dialog; when `forBooking`, skip rematch if a contact is already linked. */
  const openMatchLead = (
    property: Property,
    options?: { forBooking?: boolean },
  ) => {
    const linked = linkedContactFor(property);
    setMatchingProperty(property);
    setMatchNote("");
    setHoldUnitChecked(property.status === "Available");
    setHoldUntilLocal(toDatetimeLocalValue(defaultHoldUntilIso(48)));

    if (linked) {
      setSelectedLeadId(String(linked.id));
      setLastMatchedDealId(linked.deal_id ?? null);
      setMatchNote(linked.match_note || "");
      setMatchConfirmed(
        Boolean(options?.forBooking) ||
          property.status === "Token_Received" ||
          property.status === "On_Hold",
      );
      setNegotiatedPrice(
        String(
          linked.agreement_value != null && linked.agreement_value > 0
            ? linked.agreement_value
            : (property.base_price ?? ""),
        ),
      );
    } else {
      setSelectedLeadId("");
      setLastMatchedDealId(null);
      setMatchNote("");
      setMatchConfirmed(false);
      setNegotiatedPrice(String(property.base_price ?? ""));
    }
  };

  const handleStatusChange = async (prop: Property, status: UnitStatus) => {
    if (
      prop.status === "On_Hold" &&
      status !== "On_Hold" &&
      !canChangeHold(prop)
    ) {
      notify(HOLD_RESTRICTED_ERROR, { type: "warning" });
      return;
    }
    const result = await transitionUnitStatus(String(prop.id), status);
    if (result.ok && result.property) {
      setViewingProperty((prev) =>
        prev && String(prev.id) === String(prop.id) ? result.property! : prev,
      );
      notify(`Unit ${prop.unit_number} → ${status.replace("_", " ")}`, {
        type: "info",
      });
    } else {
      notify(
        result.error ||
          `Could not change ${prop.unit_number} to ${status.replace(/_/g, " ")}`,
        { type: "warning" },
      );
    }
  };

  const handleReleaseHold = async (prop: Property) => {
    if (!canChangeHold(prop)) {
      notify(HOLD_RESTRICTED_ERROR, { type: "warning" });
      return;
    }
    setReleaseBusyId(String(prop.id));
    try {
      const result = await releaseUnitHold(String(prop.id));
      if (result.ok) {
        notify(`Hold released on unit ${prop.unit_number}`, { type: "info" });
        const refreshed =
          result.property ||
          properties.find((p) => String(p.id) === String(prop.id));
        // Prefer transition result; fall back to Available overlay if state lagging
        const nextProp: Property = refreshed
          ? { ...refreshed, status: "Available" }
          : { ...prop, status: "Available" };
        setViewingProperty((prev) =>
          prev && String(prev.id) === String(prop.id) ? nextProp : prev,
        );
        setMatchingProperty((prev) =>
          prev && String(prev.id) === String(prop.id) ? nextProp : prev,
        );
      } else {
        notify(
          result.error || `Could not release hold on ${prop.unit_number}`,
          { type: "warning" },
        );
      }
    } finally {
      setReleaseBusyId(null);
    }
  };

  const handleUnlinkLead = async (prop: Property) => {
    if (prop.status === "On_Hold" && !canChangeHold(prop)) {
      notify(HOLD_RESTRICTED_ERROR, { type: "warning" });
      return;
    }
    if (
      !window.confirm(
        `Unlink lead from unit ${prop.unit_number}? This clears the match and releases any hold. Bookings are not cancelled.`,
      )
    ) {
      return;
    }
    setUnlinkBusyId(String(prop.id));
    try {
      const result = await unlinkLeadFromProperty(String(prop.id));
      if (result.ok) {
        notify(`Lead unlinked from unit ${prop.unit_number}`, {
          type: "info",
        });
        const refreshed =
          result.property ||
          properties.find((p) => String(p.id) === String(prop.id));
        const nextProp: Property = refreshed
          ? {
              ...refreshed,
              status:
                prop.status === "On_Hold" ? "Available" : refreshed.status,
            }
          : {
              ...prop,
              status: prop.status === "On_Hold" ? "Available" : prop.status,
            };
        setViewingProperty((prev) =>
          prev && String(prev.id) === String(prop.id) ? nextProp : prev,
        );
        setMatchingProperty((prev) =>
          prev && String(prev.id) === String(prop.id) ? nextProp : prev,
        );
      } else {
        notify(
          result.error || `Could not unlink lead from ${prop.unit_number}`,
          { type: "warning" },
        );
      }
    } finally {
      setUnlinkBusyId(null);
    }
  };

  const handleCreateBooking = async () => {
    const lead = campaignAudience.find((l) => String(l.id) === selectedLeadId);
    const linked = matchingProperty
      ? linkedContactFor(matchingProperty)
      : undefined;
    const contactId = lead?.id ?? linked?.id;
    if (!contactId || !matchingProperty) return;
    if (!matchConfirmed) {
      notify("Confirm lead match first, then create the booking.", {
        type: "warning",
      });
      return;
    }

    const listPrice = Number(matchingProperty.base_price) || 0;
    const negotiated = Number(negotiatedPrice);
    if (!Number.isFinite(negotiated) || negotiated <= 0) {
      notify("Enter a valid negotiated price greater than zero.", {
        type: "warning",
      });
      return;
    }

    const discountAmount = Math.max(0, listPrice - negotiated);
    const pendingDiscountApproval = requiresDiscountApproval(
      listPrice,
      negotiated,
    );
    const leadName =
      lead?.name ||
      [linked?.first_name, linked?.last_name].filter(Boolean).join(" ") ||
      `Contact #${contactId}`;

    setBookingBusy(true);
    try {
      const result = await createBooking({
        contactId: Number(contactId) || contactId,
        propertyId: String(matchingProperty.id),
        agreementValue: negotiated,
        listPrice,
        discountAmount,
        dealId: lastMatchedDealId ?? undefined,
      });
      if (result.ok) {
        setPopupInfo({
          title: "Booking Created",
          desc: pendingDiscountApproval
            ? `Booking created for ${leadName} on unit ${matchingProperty.unit_number}. Discount is pending manager approval.`
            : `Booking created for ${leadName} on unit ${matchingProperty.unit_number}.`,
        });
        resetMatchDialog();
      } else {
        notify(result.error || "Could not create booking", { type: "warning" });
      }
    } catch (e) {
      notify(e instanceof Error ? e.message : "Failed to create booking", {
        type: "warning",
      });
    } finally {
      setBookingBusy(false);
    }
  };

  const bookingEligible =
    !!matchingProperty &&
    BOOKING_ELIGIBLE_STATUSES.includes(matchingProperty.status);
  const listPriceForMatch = Number(matchingProperty?.base_price) || 0;
  const negotiatedNumeric = Number(negotiatedPrice);
  const matchDiscountAmount =
    Number.isFinite(negotiatedNumeric) && negotiatedNumeric > 0
      ? Math.max(0, listPriceForMatch - negotiatedNumeric)
      : 0;
  const matchDiscountPct =
    listPriceForMatch > 0 && matchDiscountAmount > 0
      ? ((matchDiscountAmount / listPriceForMatch) * 100).toFixed(2)
      : null;

  const openCreateUnit = () => {
    if (projects.length === 0) {
      notify("Create a project first, then add units to it.", {
        type: "warning",
      });
      return;
    }
    setEditingPropertyId(null);
    setBrochureUrl("");
    setNewProp({
      unit_number: "",
      configuration: "3BHK",
      property_type: "Flat",
      carpet_area_sqft: "1500",
      base_price: "15000000",
      facing: "East",
      project_id: projects[0] ? String(projects[0].id) : "",
    });
    setIsAddModalOpen(true);
  };

  const openEditUnit = (prop: Property) => {
    setEditingPropertyId(String(prop.id));
    setBrochureUrl(brochureFor(String(prop.id))?.media_url || "");
    setNewProp({
      unit_number: prop.unit_number,
      configuration: prop.configuration,
      property_type: prop.property_type,
      carpet_area_sqft: String(prop.carpet_area_sqft),
      base_price: String(prop.base_price),
      facing: prop.facing || "East",
      project_id: String(prop.project_id),
    });
    setViewingProperty(null);
    setIsAddModalOpen(true);
  };

  const handleDeleteUnit = async (prop: Property) => {
    if (
      !window.confirm(
        `Delete unit ${prop.unit_number}? Related bookings may block deletion.`,
      )
    ) {
      return;
    }
    const ok = await deleteProperty(String(prop.id));
    setViewingProperty(null);
    notify(
      ok
        ? `Unit ${prop.unit_number} deleted`
        : `Could not delete ${prop.unit_number} (check bookings / FK)`,
      { type: ok ? "info" : "warning" },
    );
  };

  const handleSaveProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingUnit(true);
    try {
      const payload = {
        unit_number: newProp.unit_number,
        configuration: newProp.configuration,
        property_type: newProp.property_type,
        carpet_area_sqft: Number(newProp.carpet_area_sqft),
        base_price: Number(newProp.base_price),
        facing: newProp.facing,
        project_id: newProp.project_id,
      };
      if (editingPropertyId) {
        const updated = await updateProperty(editingPropertyId, payload);
        if (brochureUrl.trim()) {
          await upsertPropertyBrochure(editingPropertyId, brochureUrl);
        }
        setPopupInfo({
          title: updated ? "Unit Updated" : "Update failed",
          desc: updated
            ? `Unit ${payload.unit_number} saved.`
            : "Could not update property.",
        });
      } else {
        await addProperty(payload);
        setPopupInfo({
          title: "Property Unit Added!",
          desc: `Unit ${payload.unit_number} (${payload.configuration}) saved to catalog.`,
        });
      }
      setIsAddModalOpen(false);
      setEditingPropertyId(null);
    } finally {
      setSavingUnit(false);
    }
  };

  return (
    <>
      {isMobile ? (
        <MobileHeader>
          <h1 className="text-xl font-semibold truncate">
            Properties & Inventory
          </h1>
        </MobileHeader>
      ) : null}
      <div
        className={cn(
          "p-6 space-y-6 max-w-[1600px] mx-auto",
          isMobile && "pt-18",
        )}
      >
        {/* Header Bar */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <Building2 className="w-7 h-7 text-primary" />
              Property Inventory Catalog
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Manage unit inventory, pricing, availability, and instant buyer
              lead matching.
            </p>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            {/* View Toggle */}
            <div className="flex items-center bg-muted rounded-lg p-1 border overflow-x-auto">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
                className="h-8 px-3 text-xs shrink-0"
              >
                <Grid3X3 className="w-3.5 h-3.5 mr-1" /> Grid
              </Button>
              <Button
                variant={viewMode === "table" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("table")}
                className="h-8 px-3 text-xs shrink-0"
              >
                <TableIcon className="w-3.5 h-3.5 mr-1" /> Table
              </Button>
              <Button
                variant={viewMode === "kanban" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("kanban")}
                className="h-8 px-3 text-xs shrink-0"
              >
                <Columns3 className="w-3.5 h-3.5 mr-1" /> Kanban
              </Button>
              <Button
                variant={viewMode === "stack" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("stack")}
                className="h-8 px-3 text-xs shrink-0"
              >
                <Layers className="w-3.5 h-3.5 mr-1" /> Stack
              </Button>
            </div>

            <Button
              onClick={openCreateUnit}
              className="h-9 bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add Property
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-card p-4 rounded-xl border shadow-sm space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
              <Input
                placeholder="Search Unit #, Config, Facing..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Project Filter */}
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger>
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {defaultProjects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Property Type Filter */}
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger>
                <SelectValue placeholder="All Property Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Flat">Flat / Apartment</SelectItem>
                <SelectItem value="Villa">Villa</SelectItem>
                <SelectItem value="Plot">Plot</SelectItem>
                <SelectItem value="Commercial">Commercial</SelectItem>
                <SelectItem value="Penthouse">Penthouse</SelectItem>
                <SelectItem value="Row_House">Row House</SelectItem>
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Available">Available</SelectItem>
                <SelectItem value="Token_Received">Token Received</SelectItem>
                <SelectItem value="On_Hold">On Hold</SelectItem>
                <SelectItem value="Booked">Booked</SelectItem>
                <SelectItem value="Sold">Sold</SelectItem>
              </SelectContent>
            </Select>

            {/* Budget Filter */}
            <Select value={selectedBudget} onValueChange={setSelectedBudget}>
              <SelectTrigger>
                <SelectValue placeholder="Budget" />
              </SelectTrigger>
              <SelectContent>
                {BUDGET_PRESETS.map((b) => (
                  <SelectItem key={b.value} value={b.value}>
                    {b.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex justify-between items-center text-sm text-muted-foreground">
          <span>
            Showing <strong>{filteredProperties.length}</strong> properties
          </span>
        </div>

        {filteredProperties.length === 0 ? (
          <div className="rounded-xl border border-dashed bg-muted/20 px-6 py-16 text-center space-y-3">
            <Building2 className="w-10 h-10 mx-auto text-muted-foreground" />
            <p className="text-lg font-semibold text-foreground">
              No properties yet
            </p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {projects.length === 0
                ? "Add a project first, then create units here."
                : "No units match these filters — clear filters or add a property."}
            </p>
            <Button
              onClick={openCreateUnit}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add Property
            </Button>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProperties.map((property) => {
              const linked = linkedContactFor(property);
              return (
                <Card
                  key={property.id}
                  className="group hover:shadow-md transition-all duration-200 border-muted flex flex-col justify-between overflow-hidden"
                >
                  <div>
                    <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xl font-bold">
                            {property.unit_number}
                          </CardTitle>
                          <Badge variant="outline" className="text-xs">
                            {property.property_type}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground flex items-center mt-1">
                          <Building2 className="w-3.5 h-3.5 mr-1 text-primary" />
                          {property.project?.name || "Independent Project"}
                        </p>
                      </div>
                      {getStatusBadge(property.status)}
                    </CardHeader>

                    <CardContent className="space-y-3 pb-3 text-sm">
                      <div className="grid grid-cols-2 gap-2 bg-muted/40 p-2.5 rounded-lg">
                        <div>
                          <span className="text-xs text-muted-foreground block">
                            Configuration
                          </span>
                          <span className="font-semibold text-foreground">
                            {property.configuration}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground block">
                            Carpet Area
                          </span>
                          <span className="font-semibold text-foreground flex items-center">
                            <Maximize2 className="w-3 h-3 mr-1 text-muted-foreground" />
                            {property.carpet_area_sqft} SqFt
                          </span>
                        </div>
                        {property.floor_number && (
                          <div>
                            <span className="text-xs text-muted-foreground block">
                              Floor
                            </span>
                            <span className="font-medium text-foreground">
                              {property.floor_number}th Floor
                            </span>
                          </div>
                        )}
                        {property.facing && (
                          <div>
                            <span className="text-xs text-muted-foreground block">
                              Facing
                            </span>
                            <span className="font-medium text-foreground flex items-center">
                              <Compass className="w-3 h-3 mr-1 text-muted-foreground" />
                              {property.facing}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Price Display */}
                      <div className="flex justify-between items-baseline pt-1">
                        <span className="text-xs text-muted-foreground">
                          Base Price
                        </span>
                        <span className="text-lg font-bold text-primary">
                          {formatPriceInINR(property.base_price)}
                        </span>
                      </div>

                      {/* Amenities Tags */}
                      {property.amenities && property.amenities.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          {property.amenities.map((a, i) => (
                            <span
                              key={i}
                              className="text-[10px] bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full"
                            >
                              {a}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Linked contact for matched / hold / booked units */}
                      {linked && (
                        <div className="rounded-md bg-primary/8 px-2 py-1.5 text-xs text-primary space-y-0.5">
                          <div className="flex items-center gap-1.5">
                            <UserCheck className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate font-medium">
                              {[linked.first_name, linked.last_name]
                                .filter(Boolean)
                                .join(" ") || `Contact #${linked.id}`}
                            </span>
                          </div>
                          {linked.phone && (
                            <p className="pl-5 text-[10px] text-primary/80 truncate">
                              {linked.phone}
                            </p>
                          )}
                          {linked.match_note && (
                            <p className="pl-5 text-[10px] text-muted-foreground line-clamp-2">
                              {linked.match_note}
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </div>

                  {/* CARD FOOTER */}
                  <CardFooter className="pt-3 border-t grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => setViewingProperty(property)}
                    >
                      View Details
                    </Button>
                    {property.status === "Available" ||
                    property.status === "Token_Received" ? (
                      <Button
                        size="sm"
                        className="w-full text-xs"
                        onClick={() =>
                          openMatchLead(property, {
                            forBooking: Boolean(linked),
                          })
                        }
                      >
                        <UserCheck className="w-3.5 h-3.5 mr-1" />{" "}
                        {linked ? "Book" : matchLeadButtonLabel(property)}
                      </Button>
                    ) : property.status === "On_Hold" ? (
                      canChangeHold(property) ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs"
                          disabled={releaseBusyId === String(property.id)}
                          onClick={() => handleReleaseHold(property)}
                        >
                          {releaseBusyId === String(property.id)
                            ? "Releasing…"
                            : "Release hold"}
                        </Button>
                      ) : (
                        <p
                          className="text-[11px] text-muted-foreground text-center leading-snug self-center"
                          title={HOLD_RESTRICTED_ERROR}
                        >
                          Held by another agent
                        </p>
                      )
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs border-primary/40 text-primary hover:bg-primary/10"
                        onClick={() => setCostSheetProperty(property)}
                      >
                        <Calculator className="w-3.5 h-3.5 mr-1" /> Cost Sheet
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        ) : viewMode === "table" ? (
          <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm text-left">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-semibold border-b">
                <tr>
                  <th className="p-3.5">Unit #</th>
                  <th className="p-3.5">Project</th>
                  <th className="p-3.5">Type & Config</th>
                  <th className="p-3.5">Carpet Area</th>
                  <th className="p-3.5">Facing</th>
                  <th className="p-3.5">Base Price</th>
                  <th className="p-3.5">Status</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredProperties.map((prop) => (
                  <tr
                    key={prop.id}
                    className="hover:bg-muted/30 transition-colors"
                  >
                    <td className="p-3.5 font-bold">{prop.unit_number}</td>
                    <td className="p-3.5">{prop.project?.name || "N/A"}</td>
                    <td className="p-3.5">
                      <div>{prop.configuration}</div>
                      <span className="text-xs text-muted-foreground">
                        {prop.property_type}
                      </span>
                    </td>
                    <td className="p-3.5">{prop.carpet_area_sqft} SqFt</td>
                    <td className="p-3.5">{prop.facing || "N/A"}</td>
                    <td className="p-3.5 font-semibold text-primary">
                      {formatPriceInINR(prop.base_price)}
                    </td>
                    <td className="p-3.5">{getStatusBadge(prop.status)}</td>
                    <td className="p-3.5 text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewingProperty(prop)}
                      >
                        View
                      </Button>
                      {prop.status === "Available" ||
                      prop.status === "Token_Received" ? (
                        <Button
                          size="sm"
                          onClick={() =>
                            openMatchLead(prop, {
                              forBooking: Boolean(linkedContactFor(prop)),
                            })
                          }
                        >
                          {linkedContactFor(prop)
                            ? "Book"
                            : matchLeadButtonLabel(prop)}
                        </Button>
                      ) : prop.status === "On_Hold" ? (
                        canChangeHold(prop) ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={releaseBusyId === String(prop.id)}
                            onClick={() => handleReleaseHold(prop)}
                          >
                            {releaseBusyId === String(prop.id)
                              ? "Releasing…"
                              : "Release hold"}
                          </Button>
                        ) : (
                          <span
                            className="text-[11px] text-muted-foreground"
                            title={HOLD_RESTRICTED_ERROR}
                          >
                            Held by another agent
                          </span>
                        )
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : viewMode === "stack" ? (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Status</span>
              {UNIT_STATUSES.map((status) => (
                <span key={status} className="inline-flex items-center gap-1.5">
                  <span
                    className={cn(
                      "inline-block h-3 w-3 rounded-sm",
                      UNIT_STATUS_STACK_COLORS[status].split(" ")[0],
                    )}
                  />
                  {status.replace(/_/g, " ")}
                </span>
              ))}
            </div>

            {stackMatrix.map((project) => (
              <div
                key={project.projectId}
                className="bg-card rounded-xl border shadow-sm overflow-hidden"
              >
                <div className="px-4 py-3 border-b bg-muted/40 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-primary shrink-0" />
                  <h2 className="font-semibold text-sm sm:text-base truncate">
                    {project.projectName}
                  </h2>
                </div>

                <div className="divide-y">
                  {project.towers.map((tower) => (
                    <div key={tower.tower} className="p-3 sm:p-4 space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {tower.tower === "—" ? "Units" : `Tower ${tower.tower}`}
                      </p>
                      <div className="overflow-x-auto -mx-1 px-1">
                        <div className="inline-flex flex-col gap-1.5 min-w-full">
                          {tower.floors.map((row) => (
                            <div
                              key={`${tower.tower}-${row.floorLabel}`}
                              className="flex items-center gap-2 min-w-max"
                            >
                              <span className="w-10 shrink-0 text-right text-xs font-medium text-muted-foreground tabular-nums">
                                {row.floorLabel}
                                {row.floor != null ? "F" : ""}
                              </span>
                              <div className="flex gap-1.5">
                                {row.units.map((prop) => (
                                  <button
                                    key={prop.id}
                                    type="button"
                                    title={`${prop.unit_number} · ${prop.status.replace(/_/g, " ")} · ${prop.configuration}`}
                                    onClick={() => setViewingProperty(prop)}
                                    className={cn(
                                      "min-w-[4.5rem] h-11 px-2 rounded-md text-[11px] font-semibold leading-tight",
                                      "shadow-sm ring-offset-background transition-all",
                                      "hover:ring-2 hover:ring-primary/50 hover:scale-[1.03]",
                                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                      viewingProperty?.id === prop.id &&
                                        "ring-2 ring-primary scale-[1.03]",
                                      UNIT_STATUS_STACK_COLORS[prop.status],
                                    )}
                                  >
                                    {prop.unit_number}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* KANBAN VIEW */
          <div className="flex gap-4 overflow-x-auto pb-2">
            {UNIT_STATUSES.map((status) => {
              const columnProps = filteredProperties.filter(
                (p) => p.status === status,
              );
              return (
                <div
                  key={status}
                  className="min-w-[240px] w-60 flex-shrink-0 bg-muted/30 rounded-xl border p-3 space-y-2"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wide">
                      {status.replace(/_/g, " ")}
                    </span>
                    <Badge variant="secondary" className="text-[10px]">
                      {columnProps.length}
                    </Badge>
                  </div>
                  {columnProps.map((prop) => (
                    <Card
                      key={prop.id}
                      className="border-muted shadow-sm cursor-pointer hover:border-primary/40"
                      onClick={() => setViewingProperty(prop)}
                    >
                      <CardHeader className="p-3 pb-1 space-y-0">
                        <CardTitle className="text-sm font-bold">
                          {prop.unit_number}
                        </CardTitle>
                        <p className="text-[11px] text-muted-foreground">
                          {prop.project?.name || "Project"} ·{" "}
                          {prop.configuration}
                        </p>
                      </CardHeader>
                      <CardContent className="p-3 pt-1">
                        <p className="text-sm font-semibold text-primary">
                          {formatPriceInINR(prop.base_price)}
                        </p>
                        {(prop.status === "Available" ||
                          prop.status === "Token_Received") && (
                          <Button
                            size="sm"
                            className="w-full mt-2 h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              openMatchLead(prop, {
                                forBooking: Boolean(linkedContactFor(prop)),
                              });
                            }}
                          >
                            {linkedContactFor(prop)
                              ? "Book"
                              : matchLeadButtonLabel(prop)}
                          </Button>
                        )}
                        {prop.status === "On_Hold" &&
                          (canChangeHold(prop) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full mt-2 h-7 text-xs"
                              disabled={releaseBusyId === String(prop.id)}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReleaseHold(prop);
                              }}
                            >
                              {releaseBusyId === String(prop.id)
                                ? "Releasing…"
                                : "Release hold"}
                            </Button>
                          ) : (
                            <p
                              className="mt-2 text-[11px] text-muted-foreground text-center leading-snug"
                              title={HOLD_RESTRICTED_ERROR}
                            >
                              Held by another agent
                            </p>
                          ))}
                      </CardContent>
                    </Card>
                  ))}
                  {columnProps.length === 0 && (
                    <p className="text-[11px] text-muted-foreground text-center py-6">
                      No units
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* VIEW PROPERTY DETAILS DIALOG */}
        <Dialog
          open={!!viewingProperty}
          onOpenChange={() => setViewingProperty(null)}
        >
          <DialogContent className="flex max-h-[min(90vh,720px)] w-[min(36rem,calc(100vw-2rem))] max-w-[min(36rem,calc(100vw-2rem))] flex-col gap-3 overflow-hidden p-4 sm:p-6">
            <DialogHeader className="min-w-0 shrink-0 pr-8">
              <DialogTitle className="flex items-center gap-2 text-xl min-w-0">
                <Building2 className="w-5 h-5 shrink-0 text-primary" />
                <span className="truncate">
                  Unit Details: {viewingProperty?.unit_number}
                </span>
              </DialogTitle>
              <DialogDescription className="break-words">
                {viewingProperty?.project?.name} -{" "}
                {viewingProperty?.project?.location_address}
              </DialogDescription>
            </DialogHeader>

            {viewingProperty && (
              <div className="min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto py-1 text-sm">
                <div className="flex flex-col gap-3 rounded-lg bg-muted p-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="min-w-0">
                    <span className="text-xs text-muted-foreground block">
                      Base Price
                    </span>
                    <span className="text-xl font-bold text-primary break-words">
                      {formatPriceInINR(viewingProperty.base_price)}
                    </span>
                  </div>
                  <div className="min-w-0 w-full sm:max-w-[11rem]">
                    <span className="text-xs text-muted-foreground block mb-1">
                      Status
                    </span>
                    <Select
                      value={viewingProperty.status}
                      disabled={!canChangeHold(viewingProperty)}
                      onValueChange={(v: string) =>
                        handleStatusChange(viewingProperty, v as UnitStatus)
                      }
                    >
                      <SelectTrigger className="h-8 w-full min-w-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNIT_STATUSES.filter(
                          (s) =>
                            s === viewingProperty.status ||
                            canTransitionUnitStatus(viewingProperty.status, s),
                        ).map((s) => (
                          <SelectItem key={s} value={s}>
                            {s.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {viewingProperty.status === "On_Hold" &&
                      !canChangeHold(viewingProperty) && (
                        <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                          {HOLD_RESTRICTED_ERROR}
                        </p>
                      )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 rounded-lg border p-3">
                  <div className="min-w-0">
                    <span className="text-xs text-muted-foreground block">
                      Property Type
                    </span>
                    <span className="font-semibold text-foreground break-words">
                      {viewingProperty.property_type}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs text-muted-foreground block">
                      Configuration
                    </span>
                    <span className="font-semibold text-foreground break-words">
                      {viewingProperty.configuration}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs text-muted-foreground block">
                      Carpet Area
                    </span>
                    <span className="font-semibold text-foreground break-words">
                      {viewingProperty.carpet_area_sqft} SqFt
                    </span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs text-muted-foreground block">
                      Facing Direction
                    </span>
                    <span className="font-semibold text-foreground break-words">
                      {viewingProperty.facing || "N/A"}
                    </span>
                  </div>
                  {viewingProperty.floor_number && (
                    <div className="min-w-0">
                      <span className="text-xs text-muted-foreground block">
                        Floor
                      </span>
                      <span className="font-semibold text-foreground break-words">
                        {viewingProperty.floor_number}th Floor
                      </span>
                    </div>
                  )}
                  {viewingProperty.project?.rera_number && (
                    <div className="min-w-0">
                      <span className="text-xs text-muted-foreground block">
                        Project RERA
                      </span>
                      <span className="font-semibold text-emerald-600 break-words">
                        {viewingProperty.project.rera_number}
                      </span>
                    </div>
                  )}
                </div>

                {viewingProperty.amenities &&
                  viewingProperty.amenities.length > 0 && (
                    <div className="min-w-0">
                      <span className="text-xs font-semibold text-foreground block mb-1">
                        Included Amenities
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {viewingProperty.amenities.map((a, i) => (
                          <Badge
                            key={i}
                            variant="secondary"
                            className="text-xs"
                          >
                            <Check className="w-3 h-3 mr-1 text-emerald-600" />{" "}
                            {a}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                {brochureFor(String(viewingProperty.id)) && (
                  <a
                    href={brochureFor(String(viewingProperty.id))!.media_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline font-medium inline-flex items-center gap-1"
                  >
                    Open brochure PDF
                  </a>
                )}

                {linkedContactFor(viewingProperty) && (
                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">
                      Linked Contact
                    </span>
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4 shrink-0 text-primary" />
                      <div className="min-w-0">
                        <span className="font-semibold text-foreground block truncate">
                          {[
                            linkedContactFor(viewingProperty)?.first_name,
                            linkedContactFor(viewingProperty)?.last_name,
                          ]
                            .filter(Boolean)
                            .join(" ") ||
                            `Contact #${linkedContactFor(viewingProperty)?.id}`}
                        </span>
                        {linkedContactFor(viewingProperty)?.phone && (
                          <span className="text-xs text-muted-foreground">
                            {linkedContactFor(viewingProperty)!.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    {linkedContactFor(viewingProperty)?.match_note && (
                      <div className="rounded-md border border-border/60 bg-background/60 px-2.5 py-2">
                        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground block mb-0.5">
                          Matching Note / Offer Details
                        </span>
                        <p className="text-xs text-foreground whitespace-pre-wrap">
                          {linkedContactFor(viewingProperty)!.match_note}
                        </p>
                      </div>
                    )}
                    {(linkedContactFor(viewingProperty)?.has_active_booking ||
                      viewingProperty.status === "Booked") && (
                      <p className="text-[11px] text-muted-foreground">
                        Cancel the booking to unlink this lead.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="shrink-0 !flex-col gap-2 border-t pt-3 sm:!flex-row sm:flex-wrap">
              <Button
                variant="destructive"
                className="w-full sm:mr-auto sm:w-auto"
                onClick={() =>
                  viewingProperty && handleDeleteUnit(viewingProperty)
                }
              >
                Delete
              </Button>
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setViewingProperty(null)}
              >
                Close
              </Button>
              <Button
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => viewingProperty && openEditUnit(viewingProperty)}
              >
                Edit Unit
              </Button>
              {viewingProperty?.status === "On_Hold" &&
                canChangeHold(viewingProperty) && (
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    disabled={releaseBusyId === String(viewingProperty.id)}
                    onClick={() => handleReleaseHold(viewingProperty)}
                  >
                    {releaseBusyId === String(viewingProperty.id)
                      ? "Releasing…"
                      : "Release hold"}
                  </Button>
                )}
              {viewingProperty &&
                BOOKING_ELIGIBLE_STATUSES.includes(viewingProperty.status) &&
                linkedContactFor(viewingProperty) && (
                  <Button
                    className="w-full sm:w-auto"
                    onClick={() => {
                      const prop = viewingProperty;
                      setViewingProperty(null);
                      openMatchLead(prop!, { forBooking: true });
                    }}
                  >
                    Create booking
                  </Button>
                )}
              {viewingProperty &&
                canUnlinkLead(viewingProperty) &&
                (viewingProperty.status !== "On_Hold" ||
                  canChangeHold(viewingProperty)) && (
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    disabled={unlinkBusyId === String(viewingProperty.id)}
                    onClick={() => handleUnlinkLead(viewingProperty)}
                  >
                    {unlinkBusyId === String(viewingProperty.id)
                      ? "Unlinking…"
                      : "Unlink Lead"}
                  </Button>
                )}
              {(viewingProperty?.status === "Available" ||
                viewingProperty?.status === "On_Hold" ||
                viewingProperty?.status === "Token_Received") && (
                <Button
                  variant={
                    linkedContactFor(viewingProperty!) ? "secondary" : "default"
                  }
                  className="w-full sm:w-auto"
                  onClick={() => {
                    const prop = viewingProperty;
                    setViewingProperty(null);
                    openMatchLead(prop!);
                  }}
                >
                  <UserCheck className="w-4 h-4 mr-1.5 shrink-0" />
                  {linkedContactFor(viewingProperty!)
                    ? "Change Lead"
                    : "Match to Buyer Lead"}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ADD / EDIT PROPERTY DIALOG */}
        <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary" />
                {editingPropertyId
                  ? "Edit Property Unit"
                  : "Add Property Unit to Inventory"}
              </DialogTitle>
              <DialogDescription>
                {editingPropertyId
                  ? "Update unit specs, pricing, and optional brochure URL."
                  : "Enter unit specs, pricing, and project association."}
              </DialogDescription>
            </DialogHeader>

            <form
              onSubmit={handleSaveProperty}
              className="space-y-4 py-2 text-sm"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold">Unit Number</label>
                  <Input
                    placeholder="e.g. A-1504"
                    value={newProp.unit_number}
                    onChange={(e) =>
                      setNewProp({ ...newProp, unit_number: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold">Project</label>
                  <Select
                    value={newProp.project_id}
                    onValueChange={(val: string) =>
                      setNewProp({ ...newProp, project_id: val })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Project" />
                    </SelectTrigger>
                    <SelectContent>
                      {defaultProjects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold">Property Type</label>
                  <Select
                    value={newProp.property_type}
                    onValueChange={(val: PropertyType) =>
                      setNewProp({ ...newProp, property_type: val })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Flat">Flat</SelectItem>
                      <SelectItem value="Villa">Villa</SelectItem>
                      <SelectItem value="Plot">Plot</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                      <SelectItem value="Penthouse">Penthouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold">Configuration</label>
                  <Input
                    placeholder="e.g. 3BHK + Servant"
                    value={newProp.configuration}
                    onChange={(e) =>
                      setNewProp({ ...newProp, configuration: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold">
                    Carpet Area (SqFt)
                  </label>
                  <Input
                    type="number"
                    placeholder="1500"
                    value={newProp.carpet_area_sqft}
                    onChange={(e) =>
                      setNewProp({
                        ...newProp,
                        carpet_area_sqft: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold">
                    Base Price (₹)
                  </label>
                  <Input
                    type="number"
                    placeholder="15000000"
                    value={newProp.base_price}
                    onChange={(e) =>
                      setNewProp({ ...newProp, base_price: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              {editingPropertyId && (
                <div className="space-y-1">
                  <label className="text-xs font-semibold">
                    Brochure PDF URL (optional)
                  </label>
                  <Input
                    placeholder="https://…/brochure.pdf"
                    value={brochureUrl}
                    onChange={(e) => setBrochureUrl(e.target.value)}
                  />
                </div>
              )}

              <DialogFooter className="pt-3 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={savingUnit}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {savingUnit
                    ? "Saving…"
                    : editingPropertyId
                      ? "Save Changes"
                      : "Save Property Unit"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Match Lead Modal Dialog */}
        <Dialog
          open={!!matchingProperty}
          onOpenChange={(open: boolean) => {
            if (!open) resetMatchDialog();
          }}
        >
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-primary" />
                {matchingProperty && linkedContactFor(matchingProperty)
                  ? matchConfirmed
                    ? `Book ${matchingProperty.unit_number}`
                    : `Change Lead for ${matchingProperty.unit_number}`
                  : `Match Lead for ${matchingProperty?.unit_number}`}
              </DialogTitle>
              <DialogDescription>
                {matchingProperty &&
                linkedContactFor(matchingProperty) &&
                matchConfirmed
                  ? `Complete booking for unit ${matchingProperty.unit_number} with the linked lead.`
                  : matchingProperty && linkedContactFor(matchingProperty)
                    ? `Change the buyer lead for unit ${matchingProperty.unit_number}.`
                    : `Pair unit ${matchingProperty?.unit_number} (${matchingProperty?.configuration}, ${formatPriceInINR(matchingProperty?.base_price || 0)}) with a buyer lead.`}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-3">
              <div className="bg-muted p-3 rounded-lg text-sm space-y-1">
                <p className="font-semibold text-foreground">
                  {matchingProperty?.project?.name ||
                    projects.find((p) => p.id === matchingProperty?.project_id)
                      ?.name ||
                    "Project"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {matchingProperty?.project?.location_address ||
                    projects.find((p) => p.id === matchingProperty?.project_id)
                      ?.location_address}
                </p>
                <p className="text-xs font-medium text-primary mt-1">
                  Carpet Area: {matchingProperty?.carpet_area_sqft} SqFt |
                  Facing: {matchingProperty?.facing || "N/A"}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-foreground">
                  Select Interested Lead
                </label>
                <Select
                  value={selectedLeadId}
                  onValueChange={(id: string) => {
                    setSelectedLeadId(id);
                    setLastMatchedDealId(null);
                    // Changing lead requires Match Lead again (unless same linked contact).
                    const linked = matchingProperty
                      ? linkedContactFor(matchingProperty)
                      : undefined;
                    setMatchConfirmed(
                      Boolean(linked && String(linked.id) === String(id)),
                    );
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a lead from Contacts..." />
                  </SelectTrigger>
                  <SelectContent>
                    {campaignAudience.length === 0 ? (
                      <SelectItem value="__none" disabled>
                        No contacts available
                      </SelectItem>
                    ) : (
                      campaignAudience.map((lead) => (
                        <SelectItem key={lead.id} value={String(lead.id)}>
                          {lead.name} (Budget: {lead.budget}
                          {lead.preferred_type && lead.preferred_type !== "—"
                            ? ` · ${lead.preferred_type}`
                            : ""}
                          )
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {campaignAudience.length === 0 && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">
                    No contacts yet — create or ingest leads before matching.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-foreground">
                  Matching Note / Offer Details
                </label>
                <Input
                  placeholder="e.g. Client requested 5% discount on base price"
                  value={matchNote}
                  onChange={(e) => setMatchNote(e.target.value)}
                />
              </div>

              {matchingProperty?.status === "Available" && (
                <div className="space-y-3 rounded-lg border p-3">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="hold-unit"
                      checked={holdUnitChecked}
                      onCheckedChange={(v: boolean | "indeterminate") =>
                        setHoldUnitChecked(v === true)
                      }
                    />
                    <label
                      htmlFor="hold-unit"
                      className="text-xs font-semibold text-foreground cursor-pointer"
                    >
                      Hold unit (48h)
                    </label>
                  </div>
                  {holdUnitChecked && (
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">
                        Hold until (optional)
                      </label>
                      <Input
                        type="datetime-local"
                        value={holdUntilLocal}
                        onChange={(e) => setHoldUntilLocal(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

              {selectedLeadId && matchConfirmed && bookingEligible && (
                <div className="space-y-2 rounded-lg border p-3">
                  <label className="text-xs font-semibold text-foreground">
                    Negotiated price (₹)
                  </label>
                  <Input
                    type="number"
                    min={1}
                    placeholder={String(listPriceForMatch || "")}
                    value={negotiatedPrice}
                    onChange={(e) => setNegotiatedPrice(e.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    List price: {formatPriceInINR(listPriceForMatch)}
                    {matchingProperty &&
                    linkedContactFor(matchingProperty)?.agreement_value != null
                      ? ` · Existing agreement: ${formatPriceInINR(linkedContactFor(matchingProperty)!.agreement_value!)}`
                      : ""}
                  </p>
                  {matchDiscountAmount > 0 && (
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      Discount: {formatPriceInINR(matchDiscountAmount)}
                      {matchDiscountPct != null
                        ? ` (${matchDiscountPct}%)`
                        : ""}
                    </p>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={resetMatchDialog}>
                Cancel
              </Button>
              {selectedLeadId &&
                matchingProperty &&
                matchConfirmed &&
                bookingEligible && (
                  <Button
                    variant="secondary"
                    disabled={bookingBusy || matchingBusy}
                    onClick={handleCreateBooking}
                  >
                    {bookingBusy ? "Creating…" : "Create booking"}
                  </Button>
                )}
              <Button
                disabled={!selectedLeadId || matchingBusy}
                onClick={async () => {
                  const lead = campaignAudience.find(
                    (l) => String(l.id) === selectedLeadId,
                  );
                  if (!lead || !matchingProperty) return;

                  setMatchingBusy(true);
                  try {
                    let dealId: string | number | null = null;
                    let statusAfterMatch = matchingProperty.status;

                    if (lead.id) {
                      const noteText = [
                        `[Match Lead] Unit ${matchingProperty.unit_number}`,
                        matchingProperty.configuration,
                        formatPriceInINR(matchingProperty.base_price),
                        matchNote ? `Note: ${matchNote}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ");

                      await dataProvider.create("contact_notes", {
                        data: {
                          contact_id: Number(lead.id) || lead.id,
                          text: noteText,
                          date: new Date().toISOString(),
                          status: "warm",
                        },
                      });

                      try {
                        const dealRes = await dataProvider.create("deals", {
                          data: {
                            name: `${matchingProperty.unit_number} — ${lead.name}`,
                            stage: "negotiation",
                            description: noteText,
                            amount: matchingProperty.base_price || 0,
                            property_id: matchingProperty.id,
                            contact_ids: [Number(lead.id) || lead.id],
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                            expected_closing_date: new Date(
                              Date.now() + 30 * 86400000,
                            )
                              .toISOString()
                              .slice(0, 10),
                          },
                        });
                        dealId = dealRes.data?.id ?? null;
                        setLastMatchedDealId(dealId);
                      } catch (dealErr) {
                        notify(
                          dealErr instanceof Error
                            ? `Match note saved, but deal create failed: ${dealErr.message}`
                            : "Match note saved, but deal create failed",
                          { type: "warning" },
                        );
                      }

                      if (
                        holdUnitChecked &&
                        matchingProperty.status === "Available"
                      ) {
                        const holdUntilIso = holdUntilLocal
                          ? new Date(holdUntilLocal).toISOString()
                          : defaultHoldUntilIso(48);
                        const held = await holdUnitForLead(
                          String(matchingProperty.id),
                          Number(lead.id) || lead.id,
                          holdUntilIso,
                        );
                        if (!held.ok) {
                          notify(
                            held.error || "Lead matched but unit hold failed",
                            { type: "warning" },
                          );
                        } else {
                          statusAfterMatch = "On_Hold";
                          const heldProp = held.property
                            ? { ...held.property, status: "On_Hold" as const }
                            : {
                                ...matchingProperty,
                                status: "On_Hold" as const,
                              };
                          setMatchingProperty(heldProp);
                          setViewingProperty((prev) =>
                            prev &&
                            String(prev.id) === String(matchingProperty.id)
                              ? heldProp
                              : prev,
                          );
                        }
                      }
                    }

                    setMatchConfirmed(true);
                    if (dealId != null) setLastMatchedDealId(dealId);
                    await refreshBookedContacts();

                    const canCreateBookingNext =
                      BOOKING_ELIGIBLE_STATUSES.includes(statusAfterMatch);

                    setPopupInfo({
                      title: "Lead Matched Successfully!",
                      desc: `${lead.name} was paired with unit ${matchingProperty.unit_number} (${matchingProperty.configuration}).${
                        canCreateBookingNext
                          ? " You can create a booking next."
                          : ""
                      }`,
                    });

                    if (!canCreateBookingNext) {
                      resetMatchDialog();
                    }
                  } catch (e) {
                    notify(
                      e instanceof Error
                        ? e.message
                        : "Failed to save lead match",
                      { type: "warning" },
                    );
                  } finally {
                    setMatchingBusy(false);
                  }
                }}
              >
                {matchingBusy ? "Saving…" : "Confirm Lead Match"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* COST SHEET CALCULATOR MODAL */}
        <CostSheetCalculatorModal
          property={costSheetProperty}
          isOpen={!!costSheetProperty}
          onOpenChange={(open) => {
            if (!open) setCostSheetProperty(null);
          }}
        />

        {/* ACTION POPUP MODAL */}
        <ActionPopupModal
          isOpen={!!popupInfo}
          onClose={() => setPopupInfo(null)}
          title={popupInfo?.title || ""}
          description={popupInfo?.desc || ""}
        />
      </div>
    </>
  );
};

export default PropertyCatalog;
